import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { keepPreviousData, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Suspense, useMemo, type ReactNode } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { PageLoadingSpinner } from "@/components/ui/loading-spinner";
import { useSubdomain } from "@/hooks/useSubdomain";
import NotFound from "./pages/NotFound";
const Index = lazyWithRetry(() => import("./pages/Index"), "Index");
const Browse = lazyWithRetry(() => import("./pages/Browse"), "Browse");
const PriceCheck = lazyWithRetry(() => import("./pages/PriceCheck"), "PriceCheck");
const Sell = lazyWithRetry(() => import("./pages/Sell"), "Sell");
const BulkDrafts = lazyWithRetry(() => import("./pages/BulkDrafts"), "BulkDrafts");
const DraftListings = lazyWithRetry(() => import("./pages/BulkDraftsReview"), "BulkDraftsReview");
const ListingImports = lazyWithRetry(() => import("./pages/ListingImports"), "ListingImports");
const EditListing = lazyWithRetry(() => import("./pages/EditListing"), "EditListing");
const ProductDetail = lazyWithRetry(() => import("./pages/ProductDetail"), "ProductDetail");
const PartResearchDetail = lazyWithRetry(() => import("./pages/PartResearchDetail"), "PartResearchDetail");
const PartResearchBrowse = lazyWithRetry(() => import("./pages/PartResearchBrowse"), "PartResearchBrowse");
const Manufacturers = lazyWithRetry(() => import("./pages/Manufacturers"), "Manufacturers");
const CheckoutSuccess = lazyWithRetry(() => import("./pages/CheckoutSuccess"), "CheckoutSuccess");
const Cart = lazyWithRetry(() => import("./pages/Cart"), "Cart");
const Seller = lazyWithRetry(() => import("./pages/Seller"), "Seller");
const SellerSourcing = lazyWithRetry(() => import("./pages/SellerSourcing"), "SellerSourcing");
const SignUp = lazyWithRetry(() => import("./pages/SignUp"), "SignUp");
const Login = lazyWithRetry(() => import("./pages/Login"), "Login");
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"), "ForgotPassword");
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"), "AuthCallback");
const EngineeringConsent = lazyWithRetry(() => import("./pages/EngineeringConsent"), "EngineeringConsent");
const Messages = lazyWithRetry(() => import("./pages/Messages"), "Messages");
const Notifications = lazyWithRetry(() => import("./pages/Notifications"), "Notifications");
const Account = lazyWithRetry(() => import("./pages/Account"), "Account");
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"), "Dashboard");
const Projects = lazyWithRetry(() => import("./pages/Projects"), "Projects");
const ProjectWorkspace = lazyWithRetry(() => import("./pages/ProjectWorkspace"), "ProjectWorkspace");
const Requests = lazyWithRetry(() => import("./pages/Requests"), "Requests");
const NewRequest = lazyWithRetry(() => import("./pages/NewRequest"), "NewRequest");
const RequestDetail = lazyWithRetry(() => import("./pages/RequestDetail"), "RequestDetail");
const RequestPartInfo = lazyWithRetry(() => import("./pages/RequestPartInfo"), "RequestPartInfo");
const SourcingResponse = lazyWithRetry(() => import("./pages/SourcingResponse"), "SourcingResponse");
// Lazy load heavy components
const Admin = lazyWithRetry(() => import("./pages/Admin"), "Admin");
const Contact = lazyWithRetry(() => import("./pages/Contact"), "Contact");
const SourcingHelp = lazyWithRetry(() => import("./pages/SourcingHelp"), "SourcingHelp");
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"), "TermsOfService");
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"), "PrivacyPolicy");
const Returns = lazyWithRetry(() => import("./pages/Returns"), "Returns");
const Refunds = lazyWithRetry(() => import("./pages/Refunds"), "Refunds");
const Carriers = lazyWithRetry(() => import("./pages/Carriers"), "Carriers");
const MissionStatement = lazyWithRetry(() => import("./pages/MissionStatement"), "MissionStatement");
const Pricing = lazyWithRetry(() => import("./pages/Pricing"), "Pricing");
const MCPDocumentation = lazyWithRetry(() => import("./pages/MCPDocumentation"), "MCPDocumentation");
const Discussions = lazyWithRetry(() => import("./pages/Discussions"), "Discussions");
const DiscussionThread = lazyWithRetry(() => import("./pages/DiscussionThread"), "DiscussionThread");
const BecomeSeller = lazyWithRetry(() => import("./pages/BecomeSeller"), "BecomeSeller");
const Resellers = lazyWithRetry(() => import("./pages/Resellers"), "Resellers");
const CategoryLanding = lazyWithRetry(() => import("./pages/CategoryLanding"), "CategoryLanding");
const BrandLanding = lazyWithRetry(() => import("./pages/BrandLanding"), "BrandLanding");
const ShippingLocation = lazyWithRetry(() => import("./pages/ShippingLocation"), "ShippingLocation");
const CheckoutShipping = lazyWithRetry(() => import("./pages/CheckoutShipping"), "CheckoutShipping");
const CheckoutConfirm = lazyWithRetry(() => import("./pages/CheckoutConfirm"), "CheckoutConfirm");
const RedirectToShipping = () => { window.location.replace('/checkout/shipping'); return null; };
const Relay = lazyWithRetry(() => import("./pages/Chat"), "Relay");
// TODO: Enable for next stage after launch
// const Assistant = lazyWithRetry(() => import("./pages/Assistant"), "Assistant");
// const AssistantConversation = lazyWithRetry(() => import("./pages/AssistantConversation"), "AssistantConversation");
// const Documents = lazyWithRetry(() => import("./pages/Documents"), "Documents");
const ConnectOnboarding = lazyWithRetry(() => import("./pages/ConnectOnboarding"), "ConnectOnboarding");
const SellerOnboarding = lazyWithRetry(() => import("./pages/SellerOnboarding"), "SellerOnboarding");
// Route for legacy /order/:id deep links now redirects into Dashboard
import PrivateRoute from "@/components/auth/PrivateRoute";
import { AuthProvider } from "@/components/auth/AuthProvider";
import SafariOptimizations from "@/components/performance/SafariOptimizations";
import { marketScanLayoutV2Enabled } from "@/lib/market-scan-flags";
import { marketplaceEnabled, projectsEnabled } from "@/lib/product-focus";
const Storefront = lazyWithRetry(() => import("./pages/Storefront"), "Storefront");

// Redirect /order/:id to /dashboard?section=order-details&orderId=:id
const RedirectOrderToDashboard = () => {
  const { id } = useParams();
  const target = id
    ? `/dashboard?section=order-details&orderId=${encodeURIComponent(id)}`
    : `/dashboard?section=order-details`;
  return <Navigate to={target} replace />;
};

const RedirectLegacySearch = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const legacyQuery = params.get("search");
  if (legacyQuery && !params.get("q")) params.set("q", legacyQuery);
  params.delete("search");
  return <Navigate to={`/${params.size ? `?${params.toString()}` : ""}`} replace />;
};

const RedirectMarketScanToHome = () => {
  const location = useLocation();
  return <Navigate to={`/${location.search}`} replace />;
};

const MarketplaceOnly = ({ children }: { children: ReactNode }) => {
  if (marketplaceEnabled) return <>{children}</>;
  return <RedirectMarketScanToHome />;
};

const ProjectsOnly = ({ children }: { children: ReactNode }) => {
  if (projectsEnabled) return <>{children}</>;
  return <Navigate to="/" replace />;
};

const AccountRoute = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get("reset") === "true") {
    params.delete("reset");
    return <Navigate to={`/reset-password${params.size ? `?${params}` : ""}${location.hash}`} replace />;
  }
  return <PrivateRoute element={<Account />} />;
};

const BrowseOrSearch = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const query = params.get("search") || params.get("q");
  if (query?.trim()) {
    params.delete("search");
    params.set("q", query.trim());
    return <Navigate to={`/?${params.toString()}`} replace />;
  }
  return marketplaceEnabled ? <Browse /> : <Navigate to="/" replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      placeholderData: keepPreviousData,
      // Safari-specific optimizations
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const relayEnabled = import.meta.env.VITE_RELAY_ENABLED === "true";

/**
 * When on a subdomain like acme-corp.control-exchange.com, render the Storefront
 * directly — the subdomain acts as the seller identifier. On the main domain,
 * render the full app router as normal.
 */
const SubdomainAwareRouter = () => {
  const { subdomain, isStorefrontSubdomain } = useSubdomain();

  // Subdomain mode: render storefront with subdomain as the seller identifier
  // Passes the subdomain as a virtual route param via key + URL state
  if (marketplaceEnabled && isStorefrontSubdomain && subdomain) {
    return (
      <Routes>
        {/* Product detail pages work on subdomains too */}
        <Route path="/buy/:slug/:id" element={<ProductDetail />} />
        <Route path="/p/:id" element={<ProductDetail />} />
        <Route path="/listing/:id" element={<ProductDetail />} />
        <Route path="/parts/:slug" element={<PartResearchDetail />} />
        <Route path="/cart" element={<PrivateRoute element={<Cart />} />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<AccountRoute />} />
        {/* All other paths render the storefront (pages, collections handled inside) */}
        <Route path="*" element={<StorefrontSubdomain subdomain={subdomain} />} />
      </Routes>
    );
  }

  // Main domain: full app router
  return (
    <Routes>
      <Route path="/browse" element={<BrowseOrSearch />} />
      <Route path="/search" element={marketScanLayoutV2Enabled ? <RedirectMarketScanToHome /> : <PriceCheck />} />
      <Route path="/price-check" element={<RedirectLegacySearch />} />
      <Route path="/requests" element={<MarketplaceOnly><Requests /></MarketplaceOnly>} />
      <Route path="/requests/new" element={<MarketplaceOnly><NewRequest /></MarketplaceOnly>} />
      <Route path="/requests/:id" element={<MarketplaceOnly><RequestDetail /></MarketplaceOnly>} />
      <Route path="/parts/:slug" element={<PartResearchDetail />} />
      <Route path="/research" element={<PartResearchBrowse />} />
      <Route path="/research/:id" element={<PartResearchDetail />} />
      <Route path="/manufacturers" element={<Manufacturers />} />
      <Route path="/manufacturers/:slug" element={<Manufacturers />} />
      <Route path="/category/:slug" element={<MarketplaceOnly><CategoryLanding /></MarketplaceOnly>} />
      <Route path="/brand/:slug" element={<MarketplaceOnly><BrandLanding /></MarketplaceOnly>} />
      <Route path="/request-a-part" element={<MarketplaceOnly><RequestPartInfo /></MarketplaceOnly>} />
      <Route path="/sourcing/respond" element={<MarketplaceOnly><SourcingResponse /></MarketplaceOnly>} />
      <Route path="/sell" element={<MarketplaceOnly><PrivateRoute element={<Sell />} /></MarketplaceOnly>} />
      <Route path="/sell/bulk" element={<MarketplaceOnly><PrivateRoute element={<BulkDrafts />} /></MarketplaceOnly>} />
      <Route path="/sell/drafts" element={<MarketplaceOnly><PrivateRoute element={<DraftListings />} /></MarketplaceOnly>} />
      <Route path="/sell/imports" element={<MarketplaceOnly><PrivateRoute element={<ListingImports />} /></MarketplaceOnly>} />
      <Route path="/listing/edit/:id" element={<MarketplaceOnly><EditListing /></MarketplaceOnly>} />
      <Route path="/buy/:slug/:id" element={<MarketplaceOnly><ProductDetail /></MarketplaceOnly>} />
      <Route path="/p/:id" element={<MarketplaceOnly><ProductDetail /></MarketplaceOnly>} />
      <Route path="/listing/:id" element={<MarketplaceOnly><ProductDetail /></MarketplaceOnly>} />
      <Route path="/shipping/location" element={<MarketplaceOnly><PrivateRoute element={<ShippingLocation />} /></MarketplaceOnly>} />
      <Route path="/checkout/shipping" element={<MarketplaceOnly><PrivateRoute element={<CheckoutShipping />} /></MarketplaceOnly>} />
      <Route path="/checkout/confirm" element={<MarketplaceOnly><PrivateRoute element={<CheckoutConfirm />} /></MarketplaceOnly>} />
      <Route path="/checkout" element={<MarketplaceOnly><PrivateRoute element={<RedirectToShipping />} /></MarketplaceOnly>} />
      <Route path="/checkout/success" element={<MarketplaceOnly><PrivateRoute element={<CheckoutSuccess />} /></MarketplaceOnly>} />
      <Route path="/cart" element={<MarketplaceOnly><PrivateRoute element={<Cart />} /></MarketplaceOnly>} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/oauth/engineering/consent" element={<PrivateRoute element={<EngineeringConsent />} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/messages" element={<MarketplaceOnly><PrivateRoute element={<Messages />} /></MarketplaceOnly>} />
      <Route path="/notifications" element={<MarketplaceOnly><PrivateRoute element={<Notifications />} /></MarketplaceOnly>} />
      <Route path="/account" element={<AccountRoute />} />
      <Route path="/dashboard" element={<MarketplaceOnly><PrivateRoute element={<Dashboard />} /></MarketplaceOnly>} />
      <Route path="/projects" element={<ProjectsOnly><PrivateRoute element={<Projects />} /></ProjectsOnly>} />
      <Route path="/projects/:projectId" element={<ProjectsOnly><PrivateRoute element={<ProjectWorkspace />} /></ProjectsOnly>} />
      <Route path="/order/:id" element={<MarketplaceOnly><PrivateRoute element={<RedirectOrderToDashboard />} /></MarketplaceOnly>} />
      <Route path="/admin" element={<PrivateRoute element={<Admin />} />} />
      <Route path="/seller" element={<MarketplaceOnly><Seller /></MarketplaceOnly>} />
      <Route path="/seller/sourcing" element={<MarketplaceOnly><PrivateRoute element={<SellerSourcing />} /></MarketplaceOnly>} />
      <Route path="/seller/onboarding" element={<MarketplaceOnly><PrivateRoute element={<SellerOnboarding />} /></MarketplaceOnly>} />
      <Route path="/seller/connect-onboarding" element={<MarketplaceOnly><ConnectOnboarding /></MarketplaceOnly>} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/sourcing-help" element={<SourcingHelp />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/returns" element={<MarketplaceOnly><Returns /></MarketplaceOnly>} />
      <Route path="/refunds" element={<MarketplaceOnly><Refunds /></MarketplaceOnly>} />
      <Route path="/carriers" element={<MarketplaceOnly><Carriers /></MarketplaceOnly>} />
      <Route path="/discussions" element={<MarketplaceOnly><Discussions /></MarketplaceOnly>} />
      <Route path="/discussions/:id" element={<MarketplaceOnly><DiscussionThread /></MarketplaceOnly>} />
      <Route path="/:id" element={<MarketplaceOnly><Storefront /></MarketplaceOnly>} />
      <Route path="/@:handle" element={<MarketplaceOnly><Storefront /></MarketplaceOnly>} />
      <Route path="/become-a-seller" element={<MarketplaceOnly><BecomeSeller /></MarketplaceOnly>} />
      <Route path="/resellers" element={<MarketplaceOnly><Resellers /></MarketplaceOnly>} />
      <Route path="/pricing" element={<MarketplaceOnly><Pricing /></MarketplaceOnly>} />
      {/* MCP docs hidden until fully tested */}
      {/* <Route path="/docs/mcp" element={<MCPDocumentation />} /> */}
      {relayEnabled && <Route path="/relay" element={<MarketplaceOnly><PrivateRoute element={<Relay />} /></MarketplaceOnly>} />}
      {relayEnabled && <Route path="/relay/:id" element={<MarketplaceOnly><PrivateRoute element={<Relay />} /></MarketplaceOnly>} />}
      {/* TODO: Enable for next stage after launch */}
      {/* <Route path="/assistant" element={<PrivateRoute element={<Assistant />} />} />
      <Route path="/assistant/:id" element={<PrivateRoute element={<AssistantConversation />} />} />
      <Route path="/documents" element={<PrivateRoute element={<Documents />} />} /> */}
      <Route path="/mission-statement" element={<MissionStatement />} />
      <Route path="/" element={<Index />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/**
 * Thin wrapper that passes the subdomain as a prop to the Storefront component.
 * The Storefront component already supports resolving sellers by store_slug,
 * which is what the subdomain maps to.
 */
const StorefrontSubdomain = ({ subdomain }: { subdomain: string }) => {
  // Render the Storefront page with the subdomain as the seller identifier.
  // The Storefront component fetches /api/storefront/{subdomain} which resolves via store_slug.
  return <Storefront key={subdomain} subdomainId={subdomain} />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <SafariOptimizations />
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
        <Suspense fallback={<PageLoadingSpinner />}>
        <div style={{ contain: 'layout style paint' }}>
          <SubdomainAwareRouter />
        </div>
        </Suspense>
      </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
